import { useMemo } from 'react';
import { identity, pipe } from 'ramda';
import EDView from '../../../../../BaseComponents/View/EDView';
import { vyDefToComp } from '../../../../../Utils/ObjektVyUtils';
import addCaption from './Hocs/addCaption';
import {
  IObjektVy,
  IAttributeGroup,
  IBusinessCollectionProps,
} from '../../../../../Types/ObjektVy';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import ConnectedAttributeGroup from '../../../../../Components/Attributes/Form/Components/ConnectedAttributeGroup/ConnectedAttributeGroup';
import ConnectedAttributeField from '../../../../../Components/Attributes/Form/Components/ConnectedAttributeField/ConnectedAttributeField';
import Logger, { namedError } from '../../../../../Utils/Logger';

interface IProps extends IBusinessCollectionProps {
  id: string | null;
  bmod: IClientBusinessModel;
  /** lista av vyer (en sträng kan användas som genväg för att skapa en fältvy) */
  vyer: Array<IObjektVy | IAttributeGroup | string>;
}

/**
Komponent som visar en lista av objektvyer för samma objekt
*/
const ObjektVyLista = (props: IProps) => {
  return (
    <EDView style={{ display: 'flex', flexWrap: 'wrap', margin: '0px -8px' }}>
      {props.vyer.map(vyDef => {
        if (typeof vyDef === 'string') {
          // Single Attribute Field
          return (
            <ConnectedAttributeField
              key={vyDef}
              collection={props.bmod}
              attributePath={vyDef}
              objectId={props.id}
            />
          );
        } else if (vyDef.type === 'AttributeGroup') {
          return (
            <ConnectedAttributeGroup
              key={`${vyDef.type}-${vyDef.attributeNames[0]}`}
              collection={props.bmod}
              attributePaths={vyDef.attributeNames}
              objectId={props.id}
            />
          );
        } else if (vyDef.type === 'ObjektVy') {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const Comp = useMemo(() => {
            return pipe(
              vyDefToComp(props.bmod),
              vyDef.info && vyDef.info.komponentMedRubrik
                ? identity
                : addCaption(
                    vyDef.def ? vyDef.def(props.bmod).caption : 'def saknas'
                  )
            )(vyDef);
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [props.bmod, vyDef]);

          return (
            <Comp
              key={`${vyDef.type}-${vyDef.id}-${props.id}`}
              id={props.id}
              config={props.config}
            />
          );
        } else {
          Logger.error(
            namedError(
              'ObjektVyListaTypeError',
              'Could not understand type of vyDef: ' +
                JSON.stringify(vyDef, null, 2)
            )
          );
          return <div key="Error">Error see console</div>;
        }
      })}
    </EDView>
  );
};

/**
Skapar en objektvy utifrån en lista av objektvyer.
*/
export const objektVyLista = (
  /** typ för vyn, ska kunna användas som key för vyns komponent */
  typ: string,
  /** lista av vyer, en sträng kan användas som alternativ för att skapa en fältvy */
  vyer: Array<IObjektVy | IAttributeGroup | string>,
  caption?: string
): IObjektVy => {
  return {
    type: 'ObjektVy',
    id: typ,
    info: {},
    def: bmod => ({
      caption: caption || bmod.RootType.SOLabel,
      comp: (runtimeProps: IBusinessCollectionProps) => {
        const { id, ...restProps } = runtimeProps;
        const normalizedId =
          typeof id === 'object' && id !== null ? id.id ?? null : id;

        return (
          <ObjektVyLista
            {...restProps}
            id={normalizedId}
            vyer={vyer}
            bmod={bmod}
          />
        );
      },
    }),
  };
};
